import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiClient from '../api/client';
import { THEME } from '../theme/themeColors';

const sectionTitleStyle = {
  fontSize: '12px', fontWeight: 700, color: THEME.primaryHover, marginBottom: '8px',
  borderBottom: `2px solid ${THEME.primary}`, paddingBottom: '4px'
};

const stripLabelStyle = { fontSize: '10px', fontWeight: 600, color: THEME.primaryHover };

export default function UserDetailsModal({ user, domain, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const userId = user?.szUserId;
  const companyId = user?.szCompanyId;

  useEffect(() => {
    if (!userId || !domain) return;
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await apiClient.post(`/api/users/contact-details`, {
          domain,
          userId,
          companyId,
        });
        setDetails(res.data);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load user details');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [user, domain, token, onClose]);

  if (!user) return null;

  const Field = ({ label, value }) => (
    <div>
      <div style={{ fontSize: '10px', fontWeight: 600, color: '#495057', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{
        fontSize: '13px',
        padding: '7px 10px',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        fontFamily: 'monospace',
        wordBreak: 'break-all'
      }}>
        {value || '—'}
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1060,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px'
    }} onClick={onClose}>

      <div style={{
        width: '100%', maxWidth: '620px', background: 'white', borderRadius: '10px',
        overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }} onClick={e => e.stopPropagation()}>

        <div style={{
          background: THEME.primary, color: THEME.onPrimary, padding: '12px 18px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px' }}>
                {user.szUserName} {user.szUserRole && `(${user.szUserRole})`}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.95 }}>
                {user.szCompanyName} | {user.szDomainName}
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ background: 'none', border: 'none', color: THEME.onPrimary, fontSize: '28px', marginTop: '-6px' }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{
          background: THEME.primaryLight, padding: '10px 18px',
          borderBottom: `1px solid ${THEME.alpha(0.25)}`, display: 'flex', gap: '30px', flexWrap: 'wrap'
        }}>
          <div>
            <span style={stripLabelStyle}>USER ID</span><br />
            <span style={{ fontSize: '12.5px', fontFamily: 'monospace' }}>{user.szUserId}</span>
          </div>
          <div>
            <span style={stripLabelStyle}>COMPANY ID</span><br />
            <span style={{ fontSize: '12.5px', fontFamily: 'monospace' }}>{user.szCompanyId}</span>
          </div>
          <div>
            <span style={stripLabelStyle}>COMPANY</span><br />
            <span style={{ fontSize: '12.5px' }}>{user.szCompanyName}</span>
          </div>
        </div>

        <div style={{ padding: '16px', maxHeight: '65vh', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ width: '2rem', height: '2rem', color: THEME.primary }} />
              <p style={{ marginTop: '12px' }}>Loading details...</p>
            </div>
          ) : details ? (
            <>
              <div style={{ marginBottom: '20px' }}>
                <div style={sectionTitleStyle}>
                  CONTACT DETAILS
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <Field label="PRIMARY PHONE" value={details.primaryPhone} />
                  <Field label="OFFICE PHONE" value={details.officePhone} />
                  <Field label="CALLBACK PHONE" value={details.callbackPhone} />
                  <Field label="OFFICE EMAIL" value={details.officeEmail} />
                  <Field label="USER EMAIL" value={details.userEmail} />
                </div>
              </div>

              <div>
                <div style={sectionTitleStyle}>
                  CREDENTIALS
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <Field label="USERNAME" value={details.userName} />
                  <Field label="PASSWORD" value={details.plainPassword || '••••••••'} />
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="ENCRYPTED PASSWORD" value={details.encryptedPassword} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p className="text-center text-danger">No details available</p>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #eee', background: '#f8f9fa', textAlign: 'right' }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 22px', background: '#6c757d', color: 'white',
              border: 'none', borderRadius: '6px', fontSize: '13px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}